import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { Component, EventEmitter, Output, OnInit, ViewChild, OnDestroy } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatChipEditedEvent, MatChipInputEvent } from '@angular/material/chips';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { Observable, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, switchMap, startWith } from 'rxjs/operators';
import { TbProduto } from 'src/app/api/models/statistics';
import { ProductsService } from 'src/app/api/services/products.service';
import { MatAutocompleteSelectedEvent, MatAutocompleteTrigger } from '@angular/material/autocomplete';

export interface Product {
  reference: string;
}

@Component({
    selector: 'app-chips-add-products',
    templateUrl: './chips-add-products.component.html',
    styleUrls: ['./chips-add-products.component.css'],
    standalone: false
})
export class ChipsAddProductsComponent implements OnInit, OnDestroy {
  addOnBlur = true;
  readonly separatorKeysCodes = [ENTER, COMMA] as const;
  products: Product[] = [];
  @Output() chipsChanged = new EventEmitter<string[]>();

  subscribe: Subscription = new Subscription();
  search: string = '';
  apiResponse: number = 0;
  myControl = new FormControl('');
  allProducts: string[] = [];
  productsResponse: TbProduto[] = [];
  sugestedOptions: Observable<string[]> = of([]);

  @ViewChild(MatAutocompleteTrigger) autocomplete: MatAutocompleteTrigger | undefined;
  @ViewChild('input') inputRef!: any;

  constructor(
    private announcer: LiveAnnouncer,
    private productsService: ProductsService,
  ) {}

  ngOnInit() {
    this.sugestedOptions = this.myControl.valueChanges.pipe(
      startWith(''),
      filter((value): value is string => typeof value === 'string'),
      debounceTime(300),
      distinctUntilChanged(),
      filter(value => value.length >= 3),
      switchMap(value => this.getProducts(value || '')),
      switchMap(options =>
        of(options.filter(option => !this.products.some(p => p.reference === option)))
      )
    );
  }

  // get products for autocomplete
  getProducts(value: string): Observable<string[]> {
    return this.productsService.getProductsAutoComplete(value).pipe(
      switchMap((response: TbProduto[]) => {
        this.productsResponse = response;
        this.allProducts = response.map(product => `${product.referencia} - ${product.memoria}`);
        return of(this.allProducts);
      })
    );
  }

  optionSelected(event: MatAutocompleteSelectedEvent): void {
    const fullValue = event.option.value?.trim();
    const value = fullValue?.split(' - ')[0];
  
    if (value && !this.products.find(p => p.reference === value)) {
      this.products.push({ reference: value });
      this.emitChanges();
    }
  
    this.myControl.setValue('');
    this.inputRef.nativeElement.value = '';
  }

  // Chips logic
  private emitChanges() {
    this.chipsChanged.emit(this.products.map(p => p.reference));
  }

  add(event: MatChipInputEvent): void {
    const value = (event.value || '').trim();
  
    // block manual insertion of values
    if (this.autocomplete?.panelOpen) {
      return;
    }
  
    if (value) {
      this.products.push({ reference: value });
      this.emitChanges();
    }
  
    event.chipInput!.clear();
    this.myControl.setValue('');
  }

  remove(product: Product): void {
    const index = this.products.indexOf(product);

    if (index >= 0) {
      this.products.splice(index, 1);
      this.announcer.announce(`Removed ${product.reference}`);
      this.emitChanges();
    }
  }

  edit(product: Product, event: MatChipEditedEvent) {
    const value = event.value.trim();

    if (!value) {
      this.remove(product);
      return;
    }

    const index = this.products.indexOf(product);
    if (index >= 0) {
      this.products[index].reference = value;
      this.emitChanges();
    }
  }

  ngOnDestroy() {
    if (this.subscribe){
      this.subscribe.unsubscribe();
    }
  }
}
